import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtMoney } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import StatCard from "@/components/StatCard";
import DealCard from "@/components/DealCard";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Wallet, Users2, Sparkles, TrendingUp, Bot, ArrowRight, Crown, Bell, Gift } from "lucide-react";
import { toast } from "sonner";

export default function Dashboard() {
  const { user } = useAuth();
  const [wallet, setWallet] = useState(null);
  const [feed, setFeed] = useState([]);
  const [score, setScore] = useState(null);
  const [reorder, setReorder] = useState([]);
  const [referrals, setReferrals] = useState(null);
  const [bonuses, setBonuses] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get("/wallet"),
      api.get("/catalog/feed?limit=6"),
      api.get("/referrals/dashboard"),
      api.post("/ai/reorder-suggestions"),
      api.get("/campaigns/my-bonuses"),
    ])
      .then(([w, f, r, rr, b]) => {
        setWallet(w.data);
        setFeed(f.data);
        setReferrals(r.data);
        setReorder(rr.data?.suggestions || []);
        setBonuses(b.data || []);
      })
      .catch(() => {});
  }, []);

  const computeScore = async () => {
    try {
      const { data } = await api.post("/ai/savings-score");
      setScore(data);
    } catch (e) {
      toast.error("Couldn't compute score right now.");
    }
  };

  const handleShop = async (offer) => {
    try {
      const { data } = await api.post("/wallet/click", { offer_id: offer.id });
      // Simulate a confirmed purchase (~$100) to demo cashback
      await api.post("/wallet/simulate-purchase", { offer_id: offer.id, order_amount: 100 });
      toast.success(`Cashback simulated! +${fmtMoney((100 * (offer.cashback_rate || 5)) / 200)} approved`);
      // Refresh wallet
      const { data: w } = await api.get("/wallet");
      setWallet(w);
    } catch (e) {
      toast.error("Couldn't track click.");
    }
  };

  return (
    <div data-testid="consumer-dashboard">
      <PageHeader
        eyebrow={`Welcome back, ${user?.name?.split(" ")[0]}`}
        title="Your savings overview"
        subtitle="Smart picks, pending cashback, family savings — all in one place."
        action={
          <Button
            asChild
            className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-full hidden sm:inline-flex"
            data-testid="dash-ria-cta"
          >
            <Link to="/app/ria"><Bot className="w-4 h-4 mr-2" /> Ask Ria</Link>
          </Button>
        }
      />

      {bonuses.length > 0 && (
        <div className="mb-6 rounded-2xl border border-amber-200 bg-gradient-to-r from-amber-50 via-rose-50 to-emerald-50 px-5 py-4 flex flex-wrap items-center gap-4" data-testid="welcome-bonus-banner">
          <div className="w-10 h-10 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center border border-amber-200">
            <Gift className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-display font-bold text-base text-gray-900">
              {bonuses[0].campaign?.name || "Welcome bonus"} — {fmtMoney(bonuses[0].amount)} added!
            </div>
            <div className="text-sm text-gray-700">
              {bonuses[0].campaign?.description || "Your first cashback is already in your wallet. Start shopping to earn more."}
            </div>
          </div>
          <Link to="/app/wallet" className="text-sm font-semibold text-emerald-800 hover:underline">
            View wallet →
          </Link>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard label="Approved cashback" value={fmtMoney(wallet?.wallet?.approved || 0)} icon={Wallet} accent="emerald" testId="stat-approved" />
        <StatCard label="Pending cashback" value={fmtMoney(wallet?.wallet?.pending || 0)} icon={TrendingUp} accent="sky" sublabel="Awaiting merchant confirmation" testId="stat-pending" />
        <StatCard label="Lifetime savings" value={fmtMoney(wallet?.wallet?.lifetime || 0)} icon={Sparkles} accent="amber" testId="stat-lifetime" />
        <StatCard label="Referrals" value={referrals?.active_referrals ?? 0} icon={Users2} accent="emerald" sublabel={`${fmtMoney(referrals?.total_earnings || 0)} earned`} testId="stat-referrals" />
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mb-8">
        <div className="lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display font-bold text-xl tracking-tight">Your Smart Savings Feed</h2>
            <Link to="/app/feed" className="text-sm text-emerald-700 hover:underline" data-testid="see-all-feed">See all</Link>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            {feed.slice(0, 4).map((o) => (
              <DealCard key={o.id} offer={o} onShop={handleShop} />
            ))}
          </div>
        </div>

        <div className="space-y-4">
          <Card className="border-emerald-100 shadow-none rounded-2xl gradient-emerald">
            <CardContent className="p-5">
              <div className="text-xs uppercase tracking-[0.18em] text-emerald-700 font-semibold mb-2">AI Household Savings Score</div>
              {score ? (
                <>
                  <div className="font-display font-bold text-5xl text-emerald-800" data-testid="dash-score">
                    {score.score}<span className="text-xl text-gray-500">/100</span>
                  </div>
                  <p className="text-sm text-gray-700 mt-3 leading-relaxed line-clamp-6 whitespace-pre-line">{score.narrative}</p>
                  <div className="mt-3 text-sm text-gray-700"><strong>Estimated annual savings:</strong> {fmtMoney(score.estimated_annual_savings)}</div>
                </>
              ) : (
                <>
                  <p className="text-sm text-gray-700 mt-2">Get a personalized savings analysis powered by AI.</p>
                  <Button onClick={computeScore} className="mt-4 bg-emerald-700 hover:bg-emerald-800 text-white rounded-full" data-testid="dash-compute-score">
                    Compute score <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </>
              )}
            </CardContent>
          </Card>

          <Card className="border-sky-100 shadow-none rounded-2xl gradient-sky">
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-2">
                <Bot className="w-4 h-4 text-sky-700" />
                <div className="text-xs uppercase tracking-[0.18em] text-sky-700 font-semibold">Ria suggests</div>
              </div>
              <p className="text-sm text-gray-700">
                "I noticed you haven't activated cashback at <strong>Instacart</strong> this month. Tap to grab 8% on your next order."
              </p>
              <Link to="/app/ria" className="text-sm text-sky-700 font-semibold mt-3 inline-flex items-center" data-testid="dash-ria-link">
                Open Ria <ArrowRight className="w-4 h-4 ml-1" />
              </Link>
            </CardContent>
          </Card>

          {!user?.premium && (
            <Card className="border-amber-100 shadow-none rounded-2xl gradient-amber">
              <CardContent className="p-5">
                <div className="flex items-center gap-2 mb-2">
                  <Crown className="w-4 h-4 text-amber-700" />
                  <div className="text-xs uppercase tracking-[0.18em] text-amber-700 font-semibold">Premium</div>
                </div>
                <h3 className="font-display font-bold text-lg">Earn 10% bonus cashback</h3>
                <p className="text-sm text-gray-700 mt-1">Plus AI travel concierge, priority alerts, family vault sharing.</p>
                <Button asChild className="mt-3 bg-amber-700 hover:bg-amber-800 text-white rounded-full" data-testid="dash-premium-cta">
                  <Link to="/app/premium">Upgrade <ArrowRight className="w-4 h-4 ml-1" /></Link>
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {reorder.length > 0 && (
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display font-bold text-xl tracking-tight">Smart reorder reminders</h2>
            <Link to="/app/feed" className="text-sm text-emerald-700 hover:underline">Browse more</Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {reorder.map((s, i) => (
              <Card key={i} className="border-gray-200 shadow-none rounded-2xl" data-testid={`reorder-${i}`}>
                <CardContent className="p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <Bell className="w-4 h-4 text-amber-700" />
                    <div className="text-xs uppercase tracking-[0.18em] text-amber-700 font-semibold">Reorder soon</div>
                  </div>
                  <h3 className="font-display font-bold text-base">{s.item}</h3>
                  <p className="text-sm text-gray-600 mt-1">{s.reason}</p>
                  {s.best_offer && (
                    <div className="mt-3 flex items-center gap-2 text-sm">
                      <span className="text-emerald-700 font-semibold">{s.best_offer.cashback_rate}% cashback</span>
                      <span className="text-gray-500">at {s.best_offer.merchant_name}</span>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
